import type MockAdapter from 'axios-mock-adapter'
import { auditFieldsForCreate, auditFieldsForUpdate, newId } from '../engine/context'
import { deleteRow, findRow, insertRow, readTable, updateRow } from '../store/localDb.store'
import type { LocalRow } from '../types/localDb.types'
import { fail, handle, idOf, parse } from './c07.shared'
import { applyStatus, canWrite, masterLog, myRoles, registerEntity, text, type Usage, up } from './masterData.shared'
import { referenceCounts, usageOfCounts } from './masterLocation.handler'

/**
 * MST-07 Master Area — `/api/v1/wms/master/area/*`. Menggantikan "Master Mesin Produksi" (prototype v7.19+): satu layar untuk
 * SELURUH tempat yang bukan penyimpanan (mesin, staging, staging produksi, Area ALM, karantina NG, Not Good, pemusnahan,
 * proses FG). Rak/zone/pallet/container ada di Master Lokasi Gudang.
 *
 * Penyimpanan = `warehouse_zone` (zona selain STORAGE / STORAGE_FG). Kategori dan induk TIDAK disimpan sebagai kolom baru —
 * keduanya diturunkan dari `zone_type`, `machine_id`, kode, dan gudang (DERIVE, lihat laporan 21). Area mesin = zona
 * PRODUCTION_AREA dengan `machine_id` → `mt_equipment`; menonaktifkan area mesin ikut menonaktifkan mesinnya sehingga hilang
 * dari pilihan mesin di Request Material dan Retur WIP.
 */

const PAGE = 'MST-07'
const STORAGE_TYPES = ['STORAGE', 'STORAGE_FG']

export type AreaCategoryId =
  | 'mesin'
  | 'staging'
  | 'karantina_ng'
  | 'not_good'
  | 'pemusnahan'
  | 'proses_fg'
  | 'area_alm'
  | 'staging_produksi'
  | 'lainnya'
type Tone = 'neutral' | 'info' | 'warning' | 'success' | 'danger' | 'primary'

export const AREA_CATEGORIES: { id: AreaCategoryId; name: string; tone: Tone; addable: boolean }[] = [
  { id: 'mesin', name: 'Area Mesin', tone: 'primary', addable: true },
  { id: 'staging', name: 'Area Staging', tone: 'warning', addable: true },
  { id: 'karantina_ng', name: 'Area Karantina NG', tone: 'warning', addable: false },
  { id: 'not_good', name: 'Area Not Good', tone: 'danger', addable: false },
  { id: 'pemusnahan', name: 'Area Pemusnahan', tone: 'neutral', addable: false },
  { id: 'proses_fg', name: 'Area Proses FG', tone: 'success', addable: false },
  { id: 'area_alm', name: 'Area ALM', tone: 'success', addable: false },
  { id: 'staging_produksi', name: 'Area Staging Produksi', tone: 'primary', addable: true },
  { id: 'lainnya', name: 'Lainnya', tone: 'neutral', addable: false }
]
const categoryMeta = (id: string) =>
  AREA_CATEGORIES.find(item => item.id === id) ?? AREA_CATEGORIES[AREA_CATEGORIES.length - 1]

type IndukId = 'RM' | 'FG' | 'SGI' | 'PRD'
const RM_ROLES = ['admin_wh_rm', 'wh_rm_supervisor', 'kadiv_wh_rm']
const FG_ROLES = ['admin_wh_fg', 'supervisor_wh_fg', 'kadiv_wh_fg']
const INDUK_ORDER: IndukId[] = ['RM', 'FG', 'SGI', 'PRD']
export const AREA_INDUK: Record<
  IndukId,
  { name: string; tone: Tone; wh: string | null; write: string[]; add: AreaCategoryId[] }
> = {
  RM: { name: 'WH RM', tone: 'info', wh: 'WH-RM', write: RM_ROLES, add: ['staging'] },
  FG: { name: 'WH FG', tone: 'success', wh: 'WH-FG', write: FG_ROLES, add: ['staging'] },
  SGI: { name: 'WH Sansugi', tone: 'warning', wh: 'WH-SGI', write: ['admin_wh_sansugi'], add: ['staging'] },
  PRD: {
    name: 'Area Produksi',
    tone: 'primary',
    wh: null,
    write: [...RM_ROLES, ...FG_ROLES],
    add: ['mesin', 'staging_produksi']
  }
}

const MSG_NOT_ALLOWED = 'Peran Anda tidak berwenang mengubah area ini.'

/* ------------------------------------------------------------------ turunan: kategori, induk, keterangan */

const whCodeOf = (zone: LocalRow) => String(findRow('mt_warehouses', idOf(zone.warehouse_id))?.code ?? 'WH-RM')

export function categoryOf(zone: LocalRow): AreaCategoryId {
  const type = String(zone.zone_type)
  const code = String(zone.code)
  if (type === 'QUARANTINE_NG' || type === 'ACC_TOLERANCE') return 'karantina_ng'
  if (type === 'NOT_GOOD' || type === 'NG_FG') return 'not_good'
  if (type === 'DESTROY_FG') return 'pemusnahan'
  if (type === 'RETURN_CLAIM') return 'proses_fg'
  if (type === 'ALM_AREA') return 'area_alm'
  if (['QC_STAGING', 'STAGING_RETURN', 'STAGING_FG', 'RETURN_STAGING'].includes(type)) return 'staging'
  if (type === 'PRODUCTION_AREA' || type === 'PROD_AREA') {
    if (zone.machine_id || /^AREA-MSN/.test(code)) return 'mesin'
    if (/STG/.test(code)) return 'staging_produksi'
  }
  return 'lainnya'
}

export function indukOf(zone: LocalRow): IndukId {
  const category = categoryOf(zone)
  if (category === 'mesin' || category === 'staging_produksi' || category === 'area_alm') return 'PRD'
  const wh = whCodeOf(zone)
  return wh === 'WH-FG' ? 'FG' : wh === 'WH-SGI' ? 'SGI' : 'RM'
}

const isArea = (zone: LocalRow) => !STORAGE_TYPES.includes(String(zone.zone_type))
const isLocked = (zone: LocalRow) => categoryOf(zone) === 'area_alm'
/** Tujuan yang dipaku di dalam alur (Karantina NG, Not Good, Pemusnahan, Proses FG): boleh diganti namanya, tidak dihapus. */
const isFixedByFlow = (zone: LocalRow) => !categoryMeta(categoryOf(zone)).addable

function noteOf(zone: LocalRow): string {
  const category = categoryOf(zone)
  if (category === 'area_alm') {
    if (/ALM-1$/.test(String(zone.code))) return 'Khusus slop — tujuan bawa sisa slop; discan Operator ALM 1'
    if (/ALM-2$/.test(String(zone.code))) return 'Khusus bal — tujuan bawa sisa bal; discan Operator ALM 2'
    return 'Discan Operator ALM'
  }
  if (category === 'mesin') return 'Pilihan mesin produksi dan tujuan penyerahan pack sisa'
  if (category === 'staging_produksi') return 'Pilihan tujuan penyerahan pack sisa'
  return ''
}

/* ------------------------------------------------------------------ hak */

const writeInduk = (): IndukId[] => {
  if (!canWrite(PAGE)) return []
  const roles = myRoles()
  return INDUK_ORDER.filter(key => roles.some(code => AREA_INDUK[key].write.includes(code)))
}
/** Lihat = induk yang boleh ditulis + Area Produksi (Admin Sansugi hanya melihat Area Produksi). */
const viewInduk = (): IndukId[] => {
  const write = writeInduk()
  return INDUK_ORDER.filter(key => write.includes(key) || (key === 'PRD' && myRoles().includes('admin_wh_sansugi')))
}
const canEdit = (zone: LocalRow) => writeInduk().includes(indukOf(zone))
const requireEdit = (zone: LocalRow) => {
  if (!canEdit(zone)) fail(MSG_NOT_ALLOWED, 'ERR_UNAUTHORIZED_ROLE', 403)
}

/* ------------------------------------------------------------------ pemakaian */

/** Rujukan kode area yang berupa teks (bukan FK) pada permintaan penggabungan. */
const TEXT_REFS: [string, string[]][] = [['merge_request', ['destination_code', 'destination']]]

function usageOfArea(zone: LocalRow): Usage {
  const counts = new Map<string, number>()
  const add = (collection: string, count: number) => {
    if (count) counts.set(collection, (counts.get(collection) ?? 0) + count)
  }
  referenceCounts('warehouse_zone', new Set([String(zone.id)])).forEach((count, collection) => add(collection, count))
  TEXT_REFS.forEach(([collection, fields]) =>
    add(collection, readTable(collection).filter(row => fields.some(field => up(row[field]) === up(zone.code))).length)
  )
  if (zone.machine_id)
    referenceCounts('mt_equipment', new Set([idOf(zone.machine_id)]), ['warehouse_zone']).forEach((count, collection) =>
      add(collection, count)
    )
  return usageOfCounts(counts)
}

const codeTaken = (code: string, exceptId?: string) =>
  readTable('warehouse_zone').some(zone => up(zone.code) === code && zone.id !== exceptId) ||
  readTable('mt_rack').some(rack => up(rack.code) === code)

/* ------------------------------------------------------------------ daftar & detail */

function rowOf(zone: LocalRow) {
  const category = categoryOf(zone)
  const induk = indukOf(zone)
  const usage = usageOfArea(zone)
  return {
    id: zone.id,
    code: String(zone.code),
    name: String(zone.name ?? zone.code),
    category: { id: category, name: categoryMeta(category).name, tone: categoryMeta(category).tone },
    induk: { id: induk, name: AREA_INDUK[induk].name, tone: AREA_INDUK[induk].tone },
    note: noteOf(zone),
    is_active: zone.is_active !== false,
    inactive_reason: (zone.inactive_reason as string | null) ?? null,
    used: usage.total > 0,
    locked: isLocked(zone),
    can_edit: canEdit(zone)
  }
}

const orderKey = (zone: LocalRow) => {
  const induk = INDUK_ORDER.indexOf(indukOf(zone))
  const category = AREA_CATEGORIES.findIndex(item => item.id === categoryOf(zone))
  return induk * 1000 + category * 100
}

const visibleAreas = () => {
  const allowed = new Set(viewInduk())
  return readTable('warehouse_zone')
    .filter(zone => isArea(zone) && allowed.has(indukOf(zone)))
    .sort(
      (a, b) => orderKey(a) - orderKey(b) || String(a.code).localeCompare(String(b.code), undefined, { numeric: true })
    )
}

export function areaList() {
  const zones = visibleAreas()
  const rows = zones.map(rowOf)
  const write = writeInduk()
  const addable = AREA_CATEGORIES.filter(item => item.addable)
  return {
    rows,
    total: rows.length,
    categories: AREA_CATEGORIES.filter(item => rows.some(row => row.category.id === item.id)).map(item => ({
      id: item.id,
      name: item.name,
      tone: item.tone,
      count: rows.filter(row => row.category.id === item.id).length
    })),
    induk: viewInduk().map(key => ({
      id: key,
      name: AREA_INDUK[key].name,
      tone: AREA_INDUK[key].tone,
      count: rows.filter(row => row.induk.id === key).length
    })),
    can_add: write.length > 0 && addable.length > 0,
    add_options: write.map(key => ({
      id: key,
      name: AREA_INDUK[key].name,
      categories: AREA_INDUK[key].add.map(id => ({ id, name: categoryMeta(id).name }))
    }))
  }
}

function areaById(id: unknown) {
  const zone = findRow('warehouse_zone', text(id))
  if (!zone || !isArea(zone) || !viewInduk().includes(indukOf(zone)))
    fail('Area tidak ditemukan atau sudah tidak ada.', 'ERR_NOT_FOUND', 404)
  return zone
}

export function areaDetail(params?: { id?: string }) {
  const zone = areaById(params?.id)
  const usage = usageOfArea(zone)
  const machine = zone.machine_id ? findRow('mt_equipment', idOf(zone.machine_id)) : undefined
  const locked = isLocked(zone)
  const edit = canEdit(zone)
  return {
    ...rowOf(zone),
    wh: whCodeOfLabel(zone),
    usage: { ...usage, can_delete: usage.total === 0 && !isFixedByFlow(zone) },
    code_editable: usage.total === 0 && !locked,
    machine: machine
      ? { id: machine.id, code: machine.code, name: machine.name, is_active: machine.is_active !== false }
      : null,
    can_toggle: edit && !locked,
    can_delete: edit && !locked && !isFixedByFlow(zone) && usage.total === 0,
    fixed_by_flow: isFixedByFlow(zone)
  }
}
const whCodeOfLabel = (zone: LocalRow) =>
  ({ 'WH-RM': 'WH RM', 'WH-FG': 'WH FG', 'WH-SGI': 'WH Sansugi' })[whCodeOf(zone)] ?? whCodeOf(zone)

/* ------------------------------------------------------------------ kode baru */

const PREFIX: Record<string, (induk: IndukId) => string> = {
  staging: induk => (induk === 'FG' ? 'ZSF-' : induk === 'SGI' ? 'ZSS-' : 'ZS-'),
  staging_produksi: () => 'AREA-STG-PRD-',
  mesin: () => 'AREA-'
}

export function proposeCode(induk: IndukId, category: string, name: string): string {
  const raw = text(name)
  if (!raw) return ''
  const slug = raw
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .replace(/^(AREA|ZONA|ZONE)-/, '')
  if (!slug) return ''
  let core = slug
  if (category === 'staging_produksi') core = slug.replace(/^(STAGING-PRODUKSI|STG-PRD|STAGING)-?/, '')
  if (!core && category === 'staging_produksi') {
    let n = 1
    while (codeTaken(`AREA-STG-PRD-${n}`)) n += 1
    core = String(n)
  }
  if (!core) return ''
  const prefix = (PREFIX[category] ?? PREFIX.mesin)(induk)
  let code = prefix + core
  let suffix = 2
  while (codeTaken(code)) {
    code = `${prefix}${core}-${suffix}`
    suffix += 1
  }
  return code
}

export function codePreview(params?: { induk?: string; category?: string; name?: string }) {
  const induk = text(params?.induk) as IndukId
  const category = text(params?.category)
  const code = AREA_INDUK[induk] ? proposeCode(induk, category, text(params?.name)) : ''
  const note = !code
    ? 'Isi namanya untuk melihat kodenya.'
    : category === 'mesin'
      ? 'Mesin baru ini ikut muncul di pilihan mesin pada layar permintaan dan retur tembakau, dan di pilihan tujuan penyerahan pack.'
      : category === 'staging_produksi'
        ? 'Area baru ini ikut muncul di pilihan tujuan penyerahan pack sisa ke produksi.'
        : 'Area baru ini bisa langsung dicetak stikernya.'
  return { code, note }
}

/* ------------------------------------------------------------------ simpan */

type AreaSave = { id?: string; induk?: string; category?: string; name?: string; code?: string }

function warehouseId(code: string) {
  return readTable('mt_warehouses').find(row => row.code === code)?.id ?? null
}

function createArea(body: AreaSave) {
  const induk = text(body.induk) as IndukId
  const category = text(body.category)
  if (!AREA_INDUK[induk] || !writeInduk().includes(induk)) fail(MSG_NOT_ALLOWED, 'ERR_UNAUTHORIZED_ROLE', 403)
  if (!categoryMeta(category).addable || !AREA_INDUK[induk].add.includes(category as AreaCategoryId))
    fail('Kategori ini tidak bisa ditambah dari induk tersebut.')
  const name = text(body.name)
  if (!name) fail('Namanya belum diisi.')
  const code = proposeCode(induk, category, name)
  if (!code) fail('Namanya belum diisi.')
  let machineId: string | null = null
  if (category === 'mesin') {
    const machineCode = code.replace(/^AREA-/, '')
    if (readTable('mt_equipment').some(row => up(row.code) === machineCode))
      fail(`Mesin ${machineCode} sudah terdaftar.`)
    const machine = insertRow('mt_equipment', {
      ...auditFieldsForCreate(),
      id: newId(),
      code: machineCode,
      name: name.replace(/^Area /i, ''),
      workcenter_id: null,
      is_active: true
    }) as LocalRow
    machineId = String(machine.id)
  }
  const whCode = category === 'mesin' || category === 'staging_produksi' ? 'WH-RM' : (AREA_INDUK[induk].wh ?? 'WH-RM')
  const zoneType =
    category === 'mesin' || category === 'staging_produksi'
      ? 'PRODUCTION_AREA'
      : induk === 'FG'
        ? 'STAGING_FG'
        : 'STAGING_RETURN'
  const zone = insertRow('warehouse_zone', {
    ...auditFieldsForCreate(),
    id: newId(),
    code,
    name,
    warehouse_id: warehouseId(whCode),
    zone_type: zoneType,
    machine_id: machineId,
    is_active: true,
    inactive_reason: null
  }) as LocalRow
  masterLog(
    induk === 'FG' ? 'FG' : 'RM',
    PAGE,
    'mst-add',
    code,
    `Area baru ${code} — ${name} (${categoryMeta(category).name}, induk ${AREA_INDUK[induk].name})`
  )
  return { id: zone.id, code }
}

function updateArea(body: AreaSave) {
  const zone = areaById(body.id)
  requireEdit(zone)
  const name = text(body.name)
  if (!name) fail('Namanya belum diisi.')
  const locked = isLocked(zone) || usageOfArea(zone).total > 0
  const nextCode = locked ? String(zone.code) : up(body.code) || String(zone.code)
  if (!nextCode) fail('Kodenya belum diisi.')
  const code = nextCode.replace(/\s+/g, '-')
  if (code !== zone.code && codeTaken(code, String(zone.id))) fail(`Kode ${code} sudah dipakai area lain.`)
  const before = { code: String(zone.code), name: String(zone.name) }
  updateRow('warehouse_zone', zone.id, { ...auditFieldsForUpdate(), code, name })
  if (zone.machine_id) {
    const machine = findRow('mt_equipment', idOf(zone.machine_id))
    if (machine)
      updateRow('mt_equipment', machine.id, {
        ...auditFieldsForUpdate(),
        code: code.replace(/^AREA-/, ''),
        name: name.replace(/^Area /i, '')
      })
  }
  masterLog(
    indukOf(zone) === 'FG' ? 'FG' : 'RM',
    PAGE,
    'mst-edit',
    code,
    `Ubah area ${before.code}${code !== before.code ? ` → ${code}` : ''} — nama "${name}"`
  )
  return { id: zone.id, code }
}

export function areaSave(body: AreaSave) {
  return body.id ? updateArea(body) : createArea(body)
}

export function printLog(body: { codes?: string[]; count?: number }) {
  const codes = (body.codes ?? []).map(text).filter(Boolean)
  masterLog(
    null,
    PAGE,
    'mst-print-label',
    codes[0] ?? '',
    `Cetak ${codes.length || Number(body.count) || 0} stiker area — ${codes.join(', ')}`
  )
  return { ok: true }
}

/* ------------------------------------------------------------------ status & hapus (endpoint umum) */

const syncMachine = (zone: LocalRow, active: boolean) => {
  const machine = zone.machine_id ? findRow('mt_equipment', idOf(zone.machine_id)) : undefined
  if (machine) updateRow('mt_equipment', machine.id, { ...auditFieldsForUpdate(), is_active: active })
}

registerEntity('area', {
  collection: 'warehouse_zone',
  entity: 'Area',
  page: PAGE,
  wh: null,
  labelOf: row => String(row.code),
  usage: usageOfArea,
  deletable: true,
  guardOff: row => {
    if (!canEdit(row)) return MSG_NOT_ALLOWED
    return isLocked(row) ? 'Area ALM dipaku di sistem — tidak bisa dinonaktifkan.' : null
  },
  setActive: (row, body) => {
    requireEdit(row)
    const updated = applyStatus(
      'warehouse_zone',
      { id: row.id, ...body },
      { entity: 'Area', label: String(row.code), page: PAGE, wh: indukOf(row) === 'FG' ? 'FG' : 'RM' }
    )
    syncMachine(row, body.is_active)
    return updated
  },
  remove: row => {
    requireEdit(row)
    if (isLocked(row)) fail('Area ALM dipaku di sistem — tidak bisa dihapus.')
    if (isFixedByFlow(row)) fail('Area ini dipaku di dalam alur sistem — tidak bisa dihapus, hanya dinonaktifkan.')
    const machine = row.machine_id ? findRow('mt_equipment', idOf(row.machine_id)) : undefined
    deleteRow('warehouse_zone', row.id)
    if (machine && referenceCounts('mt_equipment', new Set([String(machine.id)])).size === 0)
      deleteRow('mt_equipment', machine.id)
    masterLog(
      indukOf(row) === 'FG' ? 'FG' : 'RM',
      PAGE,
      'mst-delete',
      String(row.code),
      `Hapus area ${row.code} — belum pernah dipakai`
    )
  }
})

/* ------------------------------------------------------------------ register */

export function registerMasterAreaHandlers(mock: MockAdapter) {
  const base = '/api/v1/wms/master/area'
  mock.onGet(`${base}/list`).reply(() => handle(() => areaList()))
  mock.onGet(`${base}/detail`).reply(config => handle(() => areaDetail(config.params)))
  mock.onGet(`${base}/code-preview`).reply(config => handle(() => codePreview(config.params)))
  mock.onPost(`${base}/save`).reply(config => handle(() => areaSave(parse(config.data) as AreaSave)))
  mock.onPost(`${base}/print`).reply(config => handle(() => printLog(parse(config.data) as never)))
}
